import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Location, NgIf, NgFor, AsyncPipe } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import {
  PageDetailComponent,
  PrintPageComponent,
  SummaryCardComponent,
} from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { AgrDatePipe, LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SettingsStorage } from '@next/core-lib/settings';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { AgrUser, UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { BrandingLogoSrc } from 'src/app/branding/branding.model';
import { Cycle } from 'src/app/cycle/models/cycle.model';
import { getCycles } from 'src/app/cycle/store/selectors/cycle.selectors';
import { PoultryContextKeys } from 'src/app/shared/constants';
import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import { AdditiveComponent } from '../../../shared/components/additive/additive.component';
import { Product, ViewMode } from '../../../shared/models/shared.model';
import { AdditiveRow, FeedAdvice, ProductRow } from '../../models/feed-advice.models';
import * as feedAdviceActions from '../../store/feed-advice.actions';
import {
  getActiveAdditives,
  getActiveProducts,
  getBaseFeedAdvice,
} from '../../store/feed-advice.selectors';

import { FeedAdviceReportComponent } from '../feed-advice-report/feed-advice-report.component';

@Component({
  selector: 'app-create-feed-advice',
  templateUrl: './create-feed-advice.component.html',
  styleUrls: ['./create-feed-advice.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PageDetailComponent,
    NgIf,
    MatButton,
    MatIcon,
    PrintPageComponent,
    FeedAdviceReportComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    AgrNumberDirective,
    TableComponent,
    MatSelect,
    NgFor,
    MatOption,
    AdditiveComponent,
    SummaryCardComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class CreateFeedAdviceComponent implements AfterViewInit, OnInit, AfterViewChecked {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly locale = inject<LocaleService>(LocaleService);
  private readonly dialogService = inject(DialogService);
  private readonly settingsService = inject(SettingsServiceHelper);
  private readonly datePipe = inject(AgrDatePipe);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly brandingService = inject(BrandingService);

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));

  viewMode$: Observable<ViewMode> = this.routeUrl$.pipe(
    map((url: string) => {
      if (url.indexOf('feed-advice/view/') >= 0) {
        return ViewMode.View;
      } else if (url.endsWith('/feed-advice/create')) {
        return ViewMode.Create;
      } else {
        return ViewMode.Edit;
      }
    }),
  );

  pageTitle$: Observable<string> = this.viewMode$.pipe(
    map((viewMode: ViewMode) => {
      switch (viewMode) {
        case ViewMode.View:
          return `${this.locale.translate('feed-advice.create.title')}: ${this.feedAdvice.name}`;
        case ViewMode.Create:
          return `${this.locale.translate('add')} ${this.locale.translate(
            'feed-advice.create.title',
          )}`;
        case ViewMode.Edit:
          return `${this.locale.translate('edit')} ${this.locale.translate(
            'feed-advice.create.title',
          )}: ${this.feedAdvice.name}`;
      }
    }),
  );

  public viewModes: typeof ViewMode = ViewMode;
  products$: Observable<Product[]> = this.store.select(getActiveProducts);

  additiveRows: AdditiveRow[] = [];
  additives$: Observable<Product[]> = this.store.select(getActiveAdditives);
  user$: Observable<AgrUser> = this.store.select(UserSelectors.selectUser);
  cycles$: Observable<Cycle[]> = this.store
    .select(getCycles)
    .pipe(map((cycles) => cycles.sort((a, b) => (a.cycleNumber < b.cycleNumber ? 1 : -1))));

  /* Products */
  chickenWeightControls = new FormArray([]);
  ageFromControls = new FormArray([]);
  ageToControls = new FormArray([]);
  totalFeedControls = new FormArray([]);
  coreFeedControls = new FormArray([]);
  wheatFeedControls = new FormArray([]);
  commentControls = new FormArray([]);
  productControls = new FormArray([]);

  /* TEMPLATES */
  @ViewChild('productNameTemplate') productNameTemplate: TemplateRef<Product>;

  /* TABLE CONFIG */
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([] as ProductRow[]),
    selectColumn: false,
    pagination: false,
    columns: [],
  };

  feedAdviceGroup = new FormGroup({
    name: new FormControl(null, [Validators.required]),
    numberOfDayOldChicks: new FormControl(null, []),
    description: new FormControl(null),
  });

  isNewFeedAdvice: boolean;
  feedAdvice: FeedAdvice;
  showAdditives = true;

  showAgrifirmLogo = true;
  customizedLogoSrc: string = BrandingLogoSrc.PoultryNextLogo;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    if (this.brandingService.isTenant('Agravis')) {
      this.customizedLogoSrc = BrandingLogoSrc.AgravisLogo;
      this.showAgrifirmLogo = false;
    }
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngOnInit() {
    this.activatedRoute.data
      .pipe(withLatestFrom(this.viewMode$, this.cycles$), take(1))
      .subscribe(([response, viewMode, cycles]) => {
        this.store.select(getBaseFeedAdvice).subscribe((baseFeedAdvice) => {
          if (viewMode === ViewMode.View) {
            this.tableConfig = {
              dataSource: new MatTableDataSource([] as ProductRow[]),
              selectColumn: false,
              pagination: false,
              columns: [
                {
                  id: 'productName',
                  type: TableColumnTypes.TEMPLATE,
                  label: this.locale.translate('feed-advice.create.table-header.productName'),
                },
                {
                  id: 'chickenWeightInGrams',
                  type: TableColumnTypes.NUMBER,
                  label: this.locale.translate(
                    'feed-advice.create.table-header.chickenWeightInGrams',
                  ),
                },
                {
                  id: 'age',
                  type: TableColumnTypes.STRING,
                  label: this.locale.translate('feed-advice.create.table-header.age'),
                },
                {
                  id: 'totalFeed',
                  type: TableColumnTypes.NUMBER,
                  label: this.locale.translate('feed-advice.create.table-header.totalFeed'),
                },
                {
                  id: 'coreFeed',
                  type: TableColumnTypes.NUMBER,
                  label: this.locale.translate('feed-advice.create.table-header.coreFeed'),
                },
                {
                  id: 'wheatFeed',
                  type: TableColumnTypes.NUMBER,
                  label: this.locale.translate('feed-advice.create.table-header.wheatFeed'),
                },
                {
                  id: 'coreFeedTons',
                  type: TableColumnTypes.NUMBER,
                  label: this.locale.translate('feed-advice.create.table-header.coreFeedTons'),
                },
                {
                  id: 'wheatFeedTons',
                  type: TableColumnTypes.NUMBER,
                  label: this.locale.translate('feed-advice.create.table-header.wheatFeedTons'),
                },
                {
                  id: 'comment',
                  type: TableColumnTypes.STRING,
                  label: this.locale.translate('feed-advice.create.table-header.comment'),
                },
              ],
              scrolling: true,
              width: '1200px',
              wrapperWidth: '100%',
              dragDrop: false,
            };
          } else {
            this.tableConfig = {
              dataSource: new MatTableDataSource([] as ProductRow[]),
              selectColumn: false,
              pagination: false,
              columns: [
                {
                  id: 'productName',
                  type: TableColumnTypes.TEMPLATE,
                  label: this.locale.translate('feed-advice.create.table-header.productName'),
                  inputConfig: {
                    formArray: this.productControls,
                    options: [Validators.required],
                  },
                },
                {
                  id: 'chickenWeightInGrams',
                  type: TableColumnTypes.INPUT_NUMBER,
                  inputConfig: {
                    step: 2,
                    formArray: this.chickenWeightControls,
                    options: [Validators.required],
                  },
                  label: this.locale.translate(
                    'feed-advice.create.table-header.chickenWeightInGrams',
                  ),
                },
                {
                  id: 'ageFrom',
                  type: TableColumnTypes.INPUT_NUMBER,
                  inputConfig: {
                    step: 2,
                    formArray: this.ageFromControls,
                    options: [Validators.required, this.ageValidator('ageFrom')],
                  },
                  label: this.locale.translate('feed-advice.create.table-header.ageFrom'),
                },
                {
                  id: 'ageTo',
                  type: TableColumnTypes.INPUT_NUMBER,
                  inputConfig: {
                    step: 2,
                    formArray: this.ageToControls,
                    options: [this.ageValidator('ageTo')],
                  },
                  label: this.locale.translate('feed-advice.create.table-header.ageTo'),
                },
                {
                  id: 'totalFeed',
                  type: TableColumnTypes.INPUT_NUMBER,
                  inputConfig: {
                    step: 2,
                    formArray: this.totalFeedControls,
                    options: [Validators.required],
                    callback: (row: ProductRow) => this.calculateWheat(row),
                  },
                  label: this.locale.translate('feed-advice.create.table-header.totalFeed'),
                },
                {
                  id: 'coreFeed',
                  type: TableColumnTypes.INPUT_NUMBER,
                  inputConfig: {
                    step: 2,
                    formArray: this.coreFeedControls,
                    callback: (row: ProductRow) => this.calculateWheat(row),
                  },
                  label: this.locale.translate('feed-advice.create.table-header.coreFeed'),
                },
                {
                  id: 'wheatFeed',
                  type: TableColumnTypes.INPUT_NUMBER,
                  inputConfig: {
                    step: 2,
                    formArray: this.wheatFeedControls,
                  },
                  label: this.locale.translate('feed-advice.create.table-header.wheatFeed'),
                },
                {
                  id: 'comment',
                  type: TableColumnTypes.INPUT_STRING,
                  inputConfig: {
                    formArray: this.commentControls,
                  },
                  label: this.locale.translate('feed-advice.create.table-header.comment'),
                },
              ],
              actions: [
                {
                  id: 'delete',
                  label: '',
                  callback: (row: ProductRow) => this.deleteProductRow(row),
                  icon: 'delete',
                  disabled: () => false,
                },
              ],
              scrolling: true,
              width: '1200px',
              wrapperWidth: '100%',
              dragDrop: true,
              dragDropped: (event, row: ProductRow) => this.dragDropProductRow(event, row),
              enableArrowKeys: true,
            };
          }
          if (Object.prototype.hasOwnProperty.call(response, 'feedadvice')) {
            this.isNewFeedAdvice = false;
            this.feedAdvice = { ...(<FeedAdvice>response.feedadvice) };
            this.feedAdviceGroup.controls.name.setValue(this.feedAdvice.name);
            this.feedAdviceGroup.controls.description.setValue(this.feedAdvice.description);
            this.feedAdviceGroup.controls.numberOfDayOldChicks.setValue(
              this.feedAdvice.numberOfDayOldChicks,
            );

            if (!this.feedAdvice.numberOfDayOldChicks) {
              this.tableConfig.columns = this.tableConfig.columns.filter(
                (c) => c.id !== 'coreFeedTons' && c.id !== 'wheatFeedTons',
              );
            }

            if (
              viewMode === ViewMode.View &&
              !this.feedAdvice.productRows.some((row) => !!row.comment && row.comment.length > 0)
            ) {
              this.tableConfig.columns = this.tableConfig.columns.filter((c) => c.id !== 'comment');
            }

            this.tableConfig.dataSource.data = this.feedAdvice.productRows
              .map((p) => {
                const row = Object.assign({}, p);
                if (this.feedAdvice.numberOfDayOldChicks) {
                  row.coreFeedTons =
                    this.feedAdvice.numberOfDayOldChicks > 0 && row.coreFeed > 0
                      ? Math.round((this.feedAdvice.numberOfDayOldChicks * row.coreFeed) / 1000000)
                      : null;
                  row.wheatFeedTons =
                    this.feedAdvice.numberOfDayOldChicks > 0 && row.wheatFeed > 0
                      ? Math.round((this.feedAdvice.numberOfDayOldChicks * row.wheatFeed) / 1000000)
                      : null;
                }
                row.age = `${p.ageFrom} - ${
                  p.ageTo ?? `${this.locale.translate('departure-title')}`
                }`;
                return row;
              })
              .sort((a, b) => (a.id > b.id ? 1 : -1));
          } else {
            this.isNewFeedAdvice = true;
            if (cycles.length > 0) {
              const amountOfDayOldChicks = cycles[0].dayOldChicks.reduce(
                (amount, dayOldChick) => amount + dayOldChick.amountOfChicks,
                0,
              );
              this.feedAdviceGroup.controls.numberOfDayOldChicks.setValue(amountOfDayOldChicks);
            }
            if (baseFeedAdvice === null || baseFeedAdvice === undefined) {
              this.settingsService
                .getSingle(null, PoultryContextKeys.REFERENCE_FEED_ADVICE, SettingsStorage.AppLocal)
                .subscribe((base: FeedAdvice) => {
                  if (base !== null && base !== undefined) {
                    this.feedAdvice = {
                      ...base,
                      isReferenceScheme: false,
                      referenceSchemeId: base.id as number,
                      country: null,
                      id: 0,
                    };

                    this.feedAdviceGroup.controls.name.setValue(base?.name as string);
                    this.feedAdviceGroup.controls.description.setValue(base?.description as string);
                    this.tableConfig.dataSource.data = base.productRows as ProductRow[];
                  } else {
                    this.feedAdvice = <FeedAdvice>{};
                    this.tableConfig.dataSource.data = [
                      {
                        id: 0,
                        product: null,
                        chickenWeightInGrams: null,
                        ageFrom: null,
                        ageTo: null,
                        totalFeed: null,
                        coreFeed: null,
                        wheatFeed: null,
                        comment: null,
                      },
                    ];
                  }
                });
            } else {
              this.feedAdvice = {
                ...baseFeedAdvice,
                isReferenceScheme: false,
                referenceSchemeId: baseFeedAdvice.id,
                country: null,
                id: 0,
              };

              this.feedAdviceGroup.controls.name.setValue(baseFeedAdvice?.name);
              this.feedAdviceGroup.controls.description.setValue(baseFeedAdvice?.description);
              this.tableConfig.dataSource.data = baseFeedAdvice.productRows;
            }
          }
        });
      });
  }

  ngAfterViewInit() {
    // Set productname column template.
    const newProductConfig = { ...this.tableConfig };
    const columnProductName = newProductConfig?.columns?.find((c) => c.id === 'productName');

    columnProductName.template = this.productNameTemplate;
    this.tableConfig = newProductConfig;

    this.changeDetectorRef.detectChanges();
  }

  selectProduct(selection: MatSelectChange, productRow: ProductRow) {
    this.productControls.controls[productRow.id].setValue(<Product>selection.value);
    this.updateDataSource();
  }

  getProduct(row: ProductRow): Product {
    let foundProduct = <Product>{};
    if (row.product) {
      this.products$.pipe(take(1)).subscribe((products) => {
        foundProduct = !!products && products.find((product) => product.code === row.product.code);
      });
    }
    const productControl = new FormControl();
    productControl.setValue(foundProduct);

    if (
      this.productControls.controls[row.id] === null ||
      this.productControls.controls[row.id] === undefined
    ) {
      this.productControls.setControl(row.id, productControl);
    } else {
      this.productControls.removeAt(row.id);
      this.productControls.setControl(row.id, productControl);
    }

    return foundProduct;
  }

  addProductRow() {
    this.updateDataSource();
    this.tableConfig.dataSource.data = [
      ...this.tableConfig.dataSource.data,
      {
        id:
          this.tableConfig.dataSource.data.length > 0
            ? Math.max(...this.tableConfig.dataSource.data.map((row: ProductRow) => row.id)) + 1
            : 0,
        product: null,
        chickenWeightInGrams: null,
        ageFrom: null,
        ageTo: null,
        totalFeed: null,
        coreFeed: null,
        wheatFeed: null,
        comment: null,
      },
    ];
  }

  deleteProductRow(productRow: ProductRow) {
    this.updateDataSource();
    const productList = <ProductRow[]>[];
    let index = 0;
    // After filtering selected row out, the remaining rows get new ids starting with 0
    // because otherwise problems will arise if the user should start drag/dropping rows.
    [
      ...this.tableConfig.dataSource.data.filter((row: ProductRow) => row.id !== productRow.id),
    ].forEach((row: ProductRow) => {
      const newRow = <ProductRow>{
        ...row,
        id: index,
      };
      productList.push(newRow);
      index = index + 1;
    });
    this.tableConfig.dataSource.data = productList.sort((a: ProductRow, b: ProductRow) =>
      a.id > b.id ? 1 : -1,
    );
  }

  dragDropProductRow(event: CdkDragDrop<unknown>, productRow: ProductRow) {
    this.updateDataSource();
    // Fill product row with details from formArrays
    productRow = {
      ...productRow,
      chickenWeightInGrams: this.chickenWeightControls.controls[productRow.id].value as number,
      ageFrom: this.ageFromControls.controls[productRow.id].value as number,
      ageTo: this.ageToControls.controls[productRow.id].value as number,
      totalFeed: this.totalFeedControls.controls[productRow.id].value as number,
      coreFeed: this.coreFeedControls.controls[productRow.id].value as number,
      wheatFeed: this.wheatFeedControls.controls[productRow.id].value as number,
      comment: this.commentControls.controls[productRow.id].value as string,
    };

    const productList = <ProductRow[]>[];
    let index = 0;
    const filtered = this.tableConfig.dataSource.data.filter(
      (row: ProductRow) => row.id !== event.previousIndex,
    );
    const draggedRow = <ProductRow>{ ...productRow, id: event.currentIndex };
    // the draggedRow is put in its selected place in the array
    filtered.splice(event.currentIndex, 0, draggedRow);
    // the whole array gets new ids if needed, so that they are in ascending order with no doubles
    filtered.forEach((row: ProductRow) => {
      const newRow = <ProductRow>{
        ...row,
        id: index,
      };
      productList.push(newRow);
      index = index + 1;
    });
    this.tableConfig.dataSource.data = productList.sort((a: ProductRow, b: ProductRow) =>
      a.id > b.id ? 1 : -1,
    );
  }

  printFeedAdvice() {
    const existingTitle = document.title;
    const proposedTitle = `PoultryNEXT ${this.locale.translate(
      'feed-advice.create.title',
    )} - ${this.datePipe.transform(Date())}`;
    if (window) {
      document.title = proposedTitle;
      window.print();
      document.title = existingTitle;
    }
  }

  back(saved = false) {
    if (this.feedAdvice && saved) {
      // Simply go back to the previous page, which is the balance screen of the cycle.
      this.location.back();
    } else {
      if (this.router.navigated) {
        this.location.back();
      } else {
        this.router.navigate(['../'], { relativeTo: this.activatedRoute });
      }
    }
  }

  updateDataSource() {
    const productRows = this.tableConfig.dataSource.data.map((p: ProductRow) =>
      Object.assign({}, p),
    );

    productRows.forEach((row) => {
      row.product = this.productControls.controls[row.id].value as Product;
      row.chickenWeightInGrams = this.chickenWeightControls.controls[row.id].value as number;
      row.ageFrom = this.ageFromControls.controls[row.id].value as number;
      row.ageTo = this.ageToControls.controls[row.id].value as number;
      row.totalFeed = this.totalFeedControls.controls[row.id].value as number;
      row.coreFeed = this.coreFeedControls.controls[row.id].value as number;
      row.wheatFeed = this.wheatFeedControls.controls[row.id].value as number;
      row.comment = this.commentControls.controls[row.id].value as string;
    });

    this.tableConfig.dataSource.data = productRows;
  }

  calculateWheat(productRow: ProductRow) {
    const wheatFeed =
      (this.totalFeedControls.controls[productRow.id].value as number) -
      (this.coreFeedControls.controls[productRow.id].value as number);
    this.wheatFeedControls.controls[productRow.id].setValue(wheatFeed);
  }

  private validateFeedAdvice(): boolean {
    this.feedAdviceGroup.updateValueAndValidity();
    this.feedAdviceGroup.markAllAsTouched();
    this.ageToControls.controls.forEach((c) => c.updateValueAndValidity());
    this.chickenWeightControls.controls.forEach((c) => c.updateValueAndValidity());
    this.chickenWeightControls.markAllAsTouched();
    this.ageFromControls.controls.forEach((c) => c.updateValueAndValidity());
    this.ageFromControls.markAllAsTouched();
    this.productControls.controls.forEach((c) => {
      if (!!c.value && Object.keys(c.value).length === 0) {
        c.setErrors({ required: true });
      }
    });
    this.productControls.markAllAsTouched();
    this.totalFeedControls.controls.forEach((c) => c.updateValueAndValidity());
    this.totalFeedControls.markAllAsTouched();

    if (
      this.feedAdviceGroup.valid &&
      this.ageFromControls.controls.every((c) => c.valid) &&
      this.ageToControls.controls.every((c) => c.valid) &&
      this.chickenWeightControls.controls.every((c) => c.valid) &&
      this.productControls.controls.every((c) => c.valid) &&
      this.totalFeedControls.controls.every((c) => c.valid)
    ) {
      return true;
    }
    let description = '';
    if (!this.feedAdviceGroup.valid) {
      description += `<li>${this.locale.translate(
        'feed-advice.create.validation-error.feedadvice-name',
      )}</li>`;
    }
    if (!this.ageFromControls.controls.every((c) => c.valid)) {
      description += `<li>${this.locale.translate(
        'feed-advice.create.validation-error.age-from',
      )}</li>`;
    }
    if (!this.ageToControls.controls.every((c) => c.valid)) {
      description += `<li>${this.locale.translate(
        'feed-advice.create.validation-error.age-to',
      )}</li>`;
    }
    if (!this.chickenWeightControls.controls.every((c) => c.valid)) {
      description += `<li>${this.locale.translate(
        'feed-advice.create.validation-error.chicken-weight',
      )}</li>`;
    }
    if (!this.productControls.controls.every((c) => c.valid)) {
      description += `<li>${this.locale.translate(
        'feed-advice.create.validation-error.product',
      )}</li>`;
    }
    if (!this.totalFeedControls.controls.every((c) => c.valid)) {
      description += `<li>${this.locale.translate(
        'feed-advice.create.validation-error.total-feed',
      )}</li>`;
    }

    this.dialogService.notify({
      title: this.locale.translate('feed-advice.create.validation-error.title'),
      text: `<ul>${description}</ul>`,
    });

    return false;
  }

  save() {
    if (this.validateFeedAdvice()) {
      this.updateDataSource();
      this.user$.pipe(take(1)).subscribe((user) => {
        this.feedAdvice = {
          ...this.feedAdvice,
          updatedAdvisorName: user.name,
          createdAdvisorName: this.isNewFeedAdvice ? user.name : this.feedAdvice.createdAdvisorName,
          name: <string>this.feedAdviceGroup.controls.name.value,
          description: <string>this.feedAdviceGroup.controls.description.value,
          numberOfDayOldChicks: <number>this.feedAdviceGroup.controls.numberOfDayOldChicks.value,
          isActive: true,
          productRows: <ProductRow[]>this.tableConfig.dataSource.data.map((row: ProductRow) => ({
            id: row.id,
            product: row.product,
            chickenWeightInGrams: row.chickenWeightInGrams,
            ageFrom: row.ageFrom,
            ageTo: row.ageTo,
            totalFeed: row.totalFeed,
            coreFeed: row.coreFeed,
            wheatFeed: row.wheatFeed,
            comment: row.comment,
          })),
          additiveRows: this.showAdditives ? this.additiveRows : [],
        };

        this.store.dispatch(
          feedAdviceActions.upsertFeedAdviceRequest({ feedAdvice: this.feedAdvice }),
        );
      });
    }
  }

  ageValidator(element: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (element === 'ageFrom') {
        const index = this.ageFromControls.controls.findIndex((x) => x === control) as number;
        if (index >= 0) {
          const ageToControl = this.getControlFromFormArray(this.ageToControls, index);
          if (
            this.ageToControls.controls[index].value === null ||
            (control.value as number) < (ageToControl.value as number)
          ) {
            // validate this element in ageTo
            ageToControl.setErrors(null);
            return null;
          } else {
            ageToControl.setErrors({ age: false });
            return { age: false };
          }
        }
      } else if (element === 'ageTo') {
        const index = this.ageToControls.controls.findIndex((x) => x === control) as number;
        if (index >= 0) {
          const isLastControl = index === this.ageToControls.length - 1;
          const ageFromControl = this.getControlFromFormArray(this.ageFromControls, index);
          if (control.value && (control.value as number) > (ageFromControl.value as number)) {
            // validate this element in ageTo
            ageFromControl.setErrors(null);
            return null;
          } else if (isLastControl && !control.value) {
            ageFromControl.setErrors(null);
            return null;
          } else {
            ageFromControl.setErrors({ age: false });
            return { age: false };
          }
        }
      }
    };
  }

  getControlFromFormArray(formArray: FormArray, index: number): AbstractControl {
    return formArray.controls[index];
  }

  updateAdditiveRows(additiveRows: AdditiveRow[]) {
    this.additiveRows = additiveRows;
  }

  updateShowAdditiveRows(showAdditives: boolean) {
    this.showAdditives = showAdditives;
  }
}
